import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { adminClient } from '@/api/adminClient';

interface TTSStats {
  total: number;
  ttsSupported: number;
  ttsUnsupported: number;
  supportRate: number;
  unsupportedLanguages: Array<{ code: string; name: string; nativeName: string }>;
}

interface LanguageUpdateReport {
  hasUpdates: boolean;
  checkedAt: string;
  cachedCount: number;
  azureCount: number;
  newLanguages: Array<{ code: string; name: string; nativeName: string; ttsSupported: boolean }>;
  removedLanguages: Array<{ code: string; name: string }>;
  ttsChanges: Array<{ code: string; name: string; wasSupported: boolean; nowSupported: boolean }>;
  summary: { totalNew: number; totalRemoved: number; totalTtsChanges: number };
}

interface AudioSettingsState {
  echoCancellation: boolean;
  noiseSuppression: boolean;
  autoGainControl: boolean;
}

export default function Settings() {
  const { user, logout } = useAuth();
  const [activeSection, setActiveSection] = useState<'profile' | 'system' | 'languages' | 'about'>('profile');

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Language stats state
  const [ttsStats, setTtsStats] = useState<TTSStats | null>(null);
  const [updateReport, setUpdateReport] = useState<LanguageUpdateReport | null>(null);
  const [isCheckingUpdates, setIsCheckingUpdates] = useState(false);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [languageError, setLanguageError] = useState('');

  // Audio settings state - load from localStorage
  const AUDIO_SETTINGS_KEY = 'admin-audio-settings';
  const [audioSettings, setAudioSettings] = useState<AudioSettingsState>(() => {
    const saved = localStorage.getItem(AUDIO_SETTINGS_KEY);
    if (saved) {
      try {
        return JSON.parse(saved) as AudioSettingsState;
      } catch {
        return { echoCancellation: true, noiseSuppression: true, autoGainControl: true };
      }
    }
    return { echoCancellation: true, noiseSuppression: true, autoGainControl: true };
  });

  // Save audio settings to localStorage when changed
  useEffect(() => {
    localStorage.setItem(AUDIO_SETTINGS_KEY, JSON.stringify(audioSettings));
  }, [audioSettings]);

  // Load TTS stats when languages section is active
  useEffect(() => {
    if (activeSection === 'languages' && !ttsStats) {
      loadTTSStats();
    }
  }, [activeSection]);

  const loadTTSStats = async () => {
    setIsLoadingStats(true);
    setLanguageError('');
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/languages/tts-stats`);
      const result = await response.json();
      if (result.success) {
        setTtsStats(result.data);
      } else {
        setLanguageError('Failed to load TTS statistics');
      }
    } catch (error) {
      setLanguageError('Failed to connect to server');
    } finally {
      setIsLoadingStats(false);
    }
  };

  const checkForUpdates = async () => {
    setIsCheckingUpdates(true);
    setLanguageError('');
    try {
      const response = await adminClient.checkLanguageUpdates();
      if (response.success) {
        setUpdateReport(response.data);
      } else {
        setLanguageError('Failed to check for updates');
      }
    } catch (error: any) {
      setLanguageError(error.response?.data?.error?.message || 'Failed to check for updates');
    } finally {
      setIsCheckingUpdates(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    setIsChangingPassword(true);
    try {
      await adminClient.changePassword(currentPassword, newPassword);
      setPasswordSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      setPasswordError(error.response?.data?.error?.message || 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Settings</h1>
        <p style={styles.subtitle}>Manage your admin panel configuration</p>
      </div>

      <div style={styles.grid}>
        <nav style={styles.sidebar}>
          <button
            onClick={() => setActiveSection('profile')}
            style={{
              ...styles.navButton,
              ...(activeSection === 'profile' ? styles.navButtonActive : {}),
            }}
          >
            Profile
          </button>
          <button
            onClick={() => setActiveSection('system')}
            style={{
              ...styles.navButton,
              ...(activeSection === 'system' ? styles.navButtonActive : {}),
            }}
          >
            System
          </button>
          <button
            onClick={() => setActiveSection('languages')}
            style={{
              ...styles.navButton,
              ...(activeSection === 'languages' ? styles.navButtonActive : {}),
            }}
          >
            Languages
          </button>
          <button
            onClick={() => setActiveSection('about')}
            style={{
              ...styles.navButton,
              ...(activeSection === 'about' ? styles.navButtonActive : {}),
            }}
          >
            About
          </button>
        </nav>

        <div style={styles.content}>
          {activeSection === 'profile' && (
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>Profile Settings</h2>

              <div style={styles.card}>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Username</span>
                  <span style={styles.infoValue}>{user?.username}</span>
                </div>

                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Email</span>
                  <span style={styles.infoValue}>{user?.email}</span>
                </div>

                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Role</span>
                  <span style={{ ...styles.badge, backgroundColor: '#667eea' }}>
                    {user?.role}
                  </span>
                </div>
              </div>

              <div style={styles.card}>
                <h3 style={styles.cardTitle}>Change Password</h3>
                <p style={styles.cardDescription}>
                  Update your password to keep your account secure
                </p>

                <form onSubmit={handleChangePassword} style={styles.passwordForm}>
                  {passwordError && (
                    <div style={styles.errorAlert}>
                      {passwordError}
                    </div>
                  )}

                  {passwordSuccess && (
                    <div style={styles.successAlert}>
                      {passwordSuccess}
                    </div>
                  )}

                  <div style={styles.formGroup}>
                    <label htmlFor="currentPassword" style={styles.formLabel}>
                      Current Password
                    </label>
                    <input
                      id="currentPassword"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      disabled={isChangingPassword}
                      style={styles.formInput}
                      autoComplete="current-password"
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label htmlFor="newPassword" style={styles.formLabel}>
                      New Password
                    </label>
                    <input
                      id="newPassword"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      disabled={isChangingPassword}
                      style={styles.formInput}
                      autoComplete="new-password"
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label htmlFor="confirmPassword" style={styles.formLabel}>
                      Confirm New Password
                    </label>
                    <input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={isChangingPassword}
                      style={styles.formInput}
                      autoComplete="new-password"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isChangingPassword}
                    style={{
                      ...styles.submitButton,
                      ...(isChangingPassword ? styles.submitButtonDisabled : {}),
                    }}
                  >
                    {isChangingPassword ? 'Changing Password...' : 'Change Password'}
                  </button>
                </form>
              </div>

              <div style={styles.dangerZone}>
                <h3 style={styles.dangerTitle}>Danger Zone</h3>
                <button onClick={logout} style={styles.dangerButton}>
                  Sign Out
                </button>
              </div>
            </div>
          )}

          {activeSection === 'system' && (
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>System Settings</h2>

              <div style={styles.card}>
                <h3 style={styles.cardTitle}>API Configuration</h3>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>API URL</span>
                  <code style={styles.code}>
                    {import.meta.env.VITE_API_URL || 'http://localhost:3000'}
                  </code>
                </div>

                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>WebSocket URL</span>
                  <code style={styles.code}>
                    {import.meta.env.VITE_WS_URL || 'ws://localhost:3000'}
                  </code>
                </div>
              </div>

              <div style={styles.card}>
                <h3 style={styles.cardTitle}>Audio Settings</h3>
                <p style={styles.cardDescription}>
                  Default audio input settings for WebRTC streaming
                </p>

                <div style={styles.settingRow}>
                  <div>
                    <div style={styles.settingLabel}>Echo Cancellation</div>
                    <div style={styles.settingDescription}>
                      Remove echo from audio input
                    </div>
                  </div>
                  <label style={styles.toggle}>
                    <input
                      type="checkbox"
                      checked={audioSettings.echoCancellation}
                      onChange={(e) => setAudioSettings(prev => ({ ...prev, echoCancellation: e.target.checked }))}
                      style={styles.toggleInput}
                    />
                    <span style={{
                      ...styles.toggleSlider,
                      backgroundColor: audioSettings.echoCancellation ? '#48bb78' : '#cbd5e0',
                    }}>
                      <span style={{
                        ...styles.toggleKnob,
                        transform: audioSettings.echoCancellation ? 'translateX(24px)' : 'translateX(0)',
                      }} />
                    </span>
                  </label>
                </div>

                <div style={styles.settingRow}>
                  <div>
                    <div style={styles.settingLabel}>Noise Suppression</div>
                    <div style={styles.settingDescription}>
                      Reduce background noise
                    </div>
                  </div>
                  <label style={styles.toggle}>
                    <input
                      type="checkbox"
                      checked={audioSettings.noiseSuppression}
                      onChange={(e) => setAudioSettings(prev => ({ ...prev, noiseSuppression: e.target.checked }))}
                      style={styles.toggleInput}
                    />
                    <span style={{
                      ...styles.toggleSlider,
                      backgroundColor: audioSettings.noiseSuppression ? '#48bb78' : '#cbd5e0',
                    }}>
                      <span style={{
                        ...styles.toggleKnob,
                        transform: audioSettings.noiseSuppression ? 'translateX(24px)' : 'translateX(0)',
                      }} />
                    </span>
                  </label>
                </div>

                <div style={styles.settingRow}>
                  <div>
                    <div style={styles.settingLabel}>Auto Gain Control</div>
                    <div style={styles.settingDescription}>
                      Automatically adjust audio levels
                    </div>
                  </div>
                  <label style={styles.toggle}>
                    <input
                      type="checkbox"
                      checked={audioSettings.autoGainControl}
                      onChange={(e) => setAudioSettings(prev => ({ ...prev, autoGainControl: e.target.checked }))}
                      style={styles.toggleInput}
                    />
                    <span style={{
                      ...styles.toggleSlider,
                      backgroundColor: audioSettings.autoGainControl ? '#48bb78' : '#cbd5e0',
                    }}>
                      <span style={{
                        ...styles.toggleKnob,
                        transform: audioSettings.autoGainControl ? 'translateX(24px)' : 'translateX(0)',
                      }} />
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'languages' && (
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>Language Support</h2>

              {languageError && (
                <div style={styles.errorAlert}>{languageError}</div>
              )}

              {/* TTS Statistics Card */}
              <div style={styles.card}>
                <h3 style={styles.cardTitle}>TTS (Text-to-Speech) Support</h3>
                <p style={styles.cardDescription}>
                  Azure Neural TTS voice availability across supported languages
                </p>

                {isLoadingStats ? (
                  <div style={styles.loadingText}>Loading statistics...</div>
                ) : ttsStats ? (
                  <>
                    <div style={styles.statsGrid}>
                      <div style={styles.statBox}>
                        <div style={styles.statValue}>{ttsStats.total}</div>
                        <div style={styles.statLabel}>Total Languages</div>
                      </div>
                      <div style={{ ...styles.statBox, backgroundColor: '#f0fff4' }}>
                        <div style={{ ...styles.statValue, color: '#22543d' }}>{ttsStats.ttsSupported}</div>
                        <div style={styles.statLabel}>TTS Supported</div>
                      </div>
                      <div style={{ ...styles.statBox, backgroundColor: '#fffaf0' }}>
                        <div style={{ ...styles.statValue, color: '#744210' }}>{ttsStats.ttsUnsupported}</div>
                        <div style={styles.statLabel}>Text Only</div>
                      </div>
                      <div style={{ ...styles.statBox, backgroundColor: '#ebf8ff' }}>
                        <div style={{ ...styles.statValue, color: '#2b6cb0' }}>{ttsStats.supportRate}%</div>
                        <div style={styles.statLabel}>Support Rate</div>
                      </div>
                    </div>

                    {ttsStats.unsupportedLanguages.length > 0 && (
                      <div style={styles.unsupportedList}>
                        <h4 style={styles.listTitle}>Languages without TTS ({ttsStats.unsupportedLanguages.length})</h4>
                        <div style={styles.languageTags}>
                          {ttsStats.unsupportedLanguages.map(lang => (
                            <span key={lang.code} style={styles.languageTag} title={lang.nativeName}>
                              {lang.name} ({lang.code})
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <button onClick={loadTTSStats} style={styles.refreshButton}>
                      Refresh Stats
                    </button>
                  </>
                ) : null}
              </div>

              {/* Azure Language Updates Card */}
              <div style={styles.card}>
                <h3 style={styles.cardTitle}>Azure Language Updates</h3>
                <p style={styles.cardDescription}>
                  Check if Azure Translator has added or removed languages since last cache update
                </p>

                <button
                  onClick={checkForUpdates}
                  disabled={isCheckingUpdates}
                  style={{
                    ...styles.submitButton,
                    ...(isCheckingUpdates ? styles.submitButtonDisabled : {}),
                  }}
                >
                  {isCheckingUpdates ? 'Checking...' : 'Check for Updates'}
                </button>

                {updateReport && (
                  <div style={styles.updateReport}>
                    <div style={styles.reportHeader}>
                      <span style={{
                        ...styles.badge,
                        backgroundColor: updateReport.hasUpdates ? '#f6ad55' : '#68d391'
                      }}>
                        {updateReport.hasUpdates ? 'Updates Available' : 'Up to Date'}
                      </span>
                      <span style={styles.reportTime}>
                        Checked: {new Date(updateReport.checkedAt).toLocaleString()}
                      </span>
                    </div>

                    <div style={styles.reportStats}>
                      <div>Cached: {updateReport.cachedCount} languages</div>
                      <div>Azure: {updateReport.azureCount} languages</div>
                    </div>

                    {updateReport.newLanguages.length > 0 && (
                      <div style={styles.updateSection}>
                        <h4 style={{ ...styles.listTitle, color: '#22543d' }}>
                          ✅ New Languages ({updateReport.newLanguages.length})
                        </h4>
                        <div style={styles.languageTags}>
                          {updateReport.newLanguages.map(lang => (
                            <span key={lang.code} style={{ ...styles.languageTag, backgroundColor: '#c6f6d5' }}>
                              {lang.name} ({lang.code}) {lang.ttsSupported ? '🔊' : '📝'}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {updateReport.removedLanguages.length > 0 && (
                      <div style={styles.updateSection}>
                        <h4 style={{ ...styles.listTitle, color: '#c53030' }}>
                          ❌ Removed Languages ({updateReport.removedLanguages.length})
                        </h4>
                        <div style={styles.languageTags}>
                          {updateReport.removedLanguages.map(lang => (
                            <span key={lang.code} style={{ ...styles.languageTag, backgroundColor: '#fed7d7' }}>
                              {lang.name} ({lang.code})
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {updateReport.ttsChanges.length > 0 && (
                      <div style={styles.updateSection}>
                        <h4 style={{ ...styles.listTitle, color: '#2b6cb0' }}>
                          🔊 TTS Support Changes ({updateReport.ttsChanges.length})
                        </h4>
                        <div style={styles.languageTags}>
                          {updateReport.ttsChanges.map(change => (
                            <span key={change.code} style={{ ...styles.languageTag, backgroundColor: '#bee3f8' }}>
                              {change.name}: {change.wasSupported ? '🔊→📝' : '📝→🔊'}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {!updateReport.hasUpdates && (
                      <div style={styles.noUpdates}>
                        All language data is current. No updates needed.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeSection === 'about' && (
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>About</h2>

              <div style={styles.card}>
                <h3 style={styles.cardTitle}>KPCS & KSFM Live Translation</h3>
                <p style={styles.aboutText}>
                  Real-time translation service for the 2025 KPCS & KSFM Conference.
                  This admin panel provides tools for managing audio input, monitoring
                  translation services, and maintaining the medical glossary.
                </p>

                <div style={styles.versionInfo}>
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>Version</span>
                    <span style={styles.infoValue}>1.0.0</span>
                  </div>

                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>Build Date</span>
                    <span style={styles.infoValue}>
                      {new Date().toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>

              <div style={styles.card}>
                <h3 style={styles.cardTitle}>Features</h3>
                <ul style={styles.featureList}>
                  <li>Real-time audio streaming with WebRTC</li>
                  <li>Multi-room translation management</li>
                  <li>Live connection monitoring</li>
                  <li>Medical glossary management</li>
                  <li>System health monitoring</li>
                  <li>Error log viewer</li>
                </ul>
              </div>

              <div style={styles.card}>
                <h3 style={styles.cardTitle}>Technology Stack</h3>
                <div style={styles.techStack}>
                  <span style={styles.techBadge}>React 18</span>
                  <span style={styles.techBadge}>TypeScript</span>
                  <span style={styles.techBadge}>Vite</span>
                  <span style={styles.techBadge}>WebRTC</span>
                  <span style={styles.techBadge}>WebSocket</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '32px',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#1a202c',
    margin: '0 0 4px 0',
  },
  subtitle: {
    fontSize: '16px',
    color: '#718096',
    margin: 0,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '250px 1fr',
    gap: '32px',
  },
  sidebar: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  navButton: {
    padding: '12px 16px',
    textAlign: 'left',
    backgroundColor: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#2d3748',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  navButtonActive: {
    backgroundColor: '#667eea',
    borderColor: '#667eea',
    color: 'white',
  },
  content: {
    minHeight: '600px',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  sectionTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#1a202c',
    margin: 0,
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#2d3748',
    marginTop: 0,
    marginBottom: '16px',
  },
  cardDescription: {
    fontSize: '14px',
    color: '#718096',
    margin: '0 0 20px 0',
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
    borderBottom: '1px solid #e2e8f0',
  },
  infoLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#4a5568',
  },
  infoValue: {
    fontSize: '14px',
    color: '#2d3748',
  },
  badge: {
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    color: 'white',
    textTransform: 'uppercase',
  },
  code: {
    fontFamily: 'monospace',
    fontSize: '13px',
    color: '#4a5568',
    backgroundColor: '#f7fafc',
    padding: '4px 8px',
    borderRadius: '4px',
  },
  dangerZone: {
    backgroundColor: '#fff5f5',
    border: '1px solid #feb2b2',
    borderRadius: '12px',
    padding: '24px',
  },
  dangerTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#c53030',
    marginTop: 0,
    marginBottom: '12px',
  },
  dangerButton: {
    padding: '10px 20px',
    backgroundColor: '#f56565',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  settingRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 0',
    borderBottom: '1px solid #e2e8f0',
  },
  settingLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#2d3748',
    marginBottom: '4px',
  },
  settingDescription: {
    fontSize: '13px',
    color: '#718096',
  },
  toggle: {
    position: 'relative',
    display: 'inline-block',
    width: '48px',
    height: '24px',
    cursor: 'pointer',
  },
  toggleInput: {
    opacity: 0,
    width: 0,
    height: 0,
  },
  toggleSlider: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#cbd5e0',
    borderRadius: '24px',
    transition: 'background-color 0.3s',
  },
  toggleKnob: {
    position: 'absolute',
    top: '2px',
    left: '2px',
    width: '20px',
    height: '20px',
    backgroundColor: 'white',
    borderRadius: '50%',
    transition: 'transform 0.3s',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
  },
  aboutText: {
    fontSize: '14px',
    color: '#2d3748',
    lineHeight: '1.6',
    margin: '0 0 20px 0',
  },
  versionInfo: {
    paddingTop: '16px',
    borderTop: '1px solid #e2e8f0',
  },
  featureList: {
    margin: 0,
    paddingLeft: '20px',
    fontSize: '14px',
    color: '#2d3748',
    lineHeight: '2',
  },
  techStack: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  techBadge: {
    padding: '6px 12px',
    backgroundColor: '#e6fffa',
    color: '#234e52',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
  },
  passwordForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  formLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#2d3748',
  },
  formInput: {
    padding: '10px 12px',
    border: '1px solid #cbd5e0',
    borderRadius: '6px',
    fontSize: '14px',
    transition: 'border-color 0.2s',
    outline: 'none',
  },
  submitButton: {
    padding: '12px',
    backgroundColor: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    marginTop: '8px',
  },
  submitButtonDisabled: {
    backgroundColor: '#a0aec0',
    cursor: 'not-allowed',
  },
  errorAlert: {
    padding: '12px',
    backgroundColor: '#fee',
    border: '1px solid #fcc',
    borderRadius: '6px',
    color: '#c00',
    fontSize: '14px',
  },
  successAlert: {
    padding: '12px',
    backgroundColor: '#f0fff4',
    border: '1px solid #9ae6b4',
    borderRadius: '6px',
    color: '#22543d',
    fontSize: '14px',
  },
  loadingText: {
    padding: '20px',
    textAlign: 'center',
    color: '#718096',
    fontSize: '14px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
    marginBottom: '24px',
  },
  statBox: {
    padding: '16px',
    backgroundColor: '#f7fafc',
    borderRadius: '8px',
    textAlign: 'center',
  },
  statValue: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#2d3748',
  },
  statLabel: {
    fontSize: '12px',
    color: '#718096',
    marginTop: '4px',
  },
  unsupportedList: {
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #e2e8f0',
  },
  listTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#4a5568',
    margin: '0 0 12px 0',
  },
  languageTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  languageTag: {
    padding: '4px 10px',
    backgroundColor: '#edf2f7',
    borderRadius: '4px',
    fontSize: '12px',
    color: '#4a5568',
  },
  refreshButton: {
    marginTop: '16px',
    padding: '8px 16px',
    backgroundColor: '#edf2f7',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '14px',
    color: '#4a5568',
    cursor: 'pointer',
  },
  updateReport: {
    marginTop: '20px',
    padding: '16px',
    backgroundColor: '#f7fafc',
    borderRadius: '8px',
  },
  reportHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  reportTime: {
    fontSize: '12px',
    color: '#718096',
  },
  reportStats: {
    display: 'flex',
    gap: '24px',
    marginBottom: '16px',
    fontSize: '14px',
    color: '#4a5568',
  },
  updateSection: {
    marginTop: '16px',
    paddingTop: '12px',
    borderTop: '1px solid #e2e8f0',
  },
  noUpdates: {
    marginTop: '12px',
    padding: '12px',
    backgroundColor: '#f0fff4',
    borderRadius: '6px',
    color: '#22543d',
    fontSize: '14px',
    textAlign: 'center',
  },
};
